import { getHeadersWithToken, invalidateTokenCache } from "./api";
import { FlyoutManager } from "./flyoutManager";
import { pushEvent } from "./analytics";
import { showToast } from './toast';

export function initSignin() {
    const flyout = document.querySelector('[data-flyout="SignInForm"]');
    if (!flyout) return;

    const emailInput = flyout.querySelector("#signin-email");
    const passwordInput = flyout.querySelector("#signin-password");
    const eyeIcon = flyout.querySelector(".signin-flyout__eye-icon");
    const submitButton = flyout.querySelector('button[type="submit"]');

    eyeIcon?.addEventListener("click", () => {
        const isPassword = passwordInput.type === "password";
        passwordInput.type = isPassword ? "text" : "password";
        eyeIcon.alt = isPassword ? "Hide password" : "Show password";
        eyeIcon.src = isPassword ? "/images/eye-icon-hide.svg" : "/images/eye-icon.svg";
    });

    const toggleSubmitState = () => {
        const emailFilled = emailInput?.value.trim().length > 0;
        const passwordFilled = passwordInput?.value.trim().length > 0;
        submitButton.disabled = !(emailFilled && passwordFilled);
    };

    emailInput?.addEventListener("input", () => {
        clearError(emailInput);
        toggleSubmitState();
    });

    passwordInput?.addEventListener("input", () => {
        clearError(passwordInput);
        toggleSubmitState();
    });

    function showError(field, message) {
        if (!field) return;
        field.classList.add("error");
        const errorEl = flyout.querySelector(`.error-message[data-error-for="${field.id}"]`);
        if (errorEl) {
            errorEl.textContent = message;
            errorEl.classList.add("visible");
        }
    }

    function clearError(field) {
        if (!field) return;
        field.classList.remove("error");
        const errorEl = flyout.querySelector(`.error-message[data-error-for="${field.id}"]`);
        if (errorEl) {
            errorEl.textContent = "";
            errorEl.classList.remove("visible");
        }
    }

    flyout.querySelector("form")?.addEventListener("submit", async (e) => {
        e.preventDefault();

        const email = emailInput?.value.trim();
        const password = passwordInput?.value.trim();

        clearError(emailInput);
        clearError(passwordInput);

        let valid = true;
        if (!email) {
            showError(emailInput, "Email is required.");
            valid = false;
        } else {
            const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailPattern.test(email)) {
                showError(emailInput, "Please enter a valid email address.");
                valid = false;
            }
        }

        if (!password) {
            showError(passwordInput, "Password is required.");
            valid = false;
        }

        if (!valid) return;

        submitButton.disabled = true;
        submitButton.textContent = "Signing In...";

        try {
            const payload = {
                username: email,
                password: password
            };

            const response = await fetch("/api/Accounts/SignIn", {
                method: "POST",
                credentials: "include",
                headers: await getHeadersWithToken({
                    "Content-Type": "application/json"
                }),
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                // GTM: Track successful login
                pushEvent({
                    'event': 'formSubmission',
                    'formID': 'login'
                });

                FlyoutManager.close("SignInForm");
                
                // notify other components that the user signed in so that they can react,
                // the header specifically uses this
                invalidateTokenCache(); // the xsrf token needs to be refreshed after sign in/out
                document.dispatchEvent(new Event('mm.signedIn'));
            }
            else {
                const rawText = await response.text();
                console.warn("Raw error response text:", rawText);

                try {
                    const result = JSON.parse(rawText);
                    console.warn("Parsed sign-in error response:", result);

                    if (result.errors) {
                        if (result.errors.username) {
                            showError(emailInput, result.errors.username.join(" "));
                        }
                        if (result.errors.password) {
                            showError(passwordInput, result.errors.password.join(" "));
                        }
                        if (!result.errors.username && !result.errors.password && result.message) {
                            showToast(result.message, 'error');
                        }
                    } else if (result.message) {
                        showToast(result.message, 'error');
                    } else {
                        showToast("Invalid email or password. Please try again.", 'error');
                    }
                } catch (jsonErr) {
                    console.error("Could not parse error response as JSON:", jsonErr);
                    showToast("An unexpected error occurred. Please try again.", 'error');
                }
            }
        } catch (err) {
            console.error("Sign-in error:", err);
            showToast("Something went wrong. Please try again.", 'error');
        } finally {
            submitButton.disabled = false;
            submitButton.textContent = "Sign In";
        }
    });
}

export function showSignInFlyout() {
    FlyoutManager.open("SignInForm");
}
